import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { allergyService } from '../services/allergyService'
import { vaccineReferenceService } from '../services/vaccineReferenceService'
// Types imported but not directly used in component
import './VaccineAllergyChecker.css'

interface VaccineAllergyCheckerProps {
  patientId: string
  vaccineType: string
  onWarningsChange?: (warnings: string[]) => void
}

export function VaccineAllergyChecker({
  patientId,
  vaccineType,
  onWarningsChange,
}: VaccineAllergyCheckerProps) {
  const [warnings, setWarnings] = useState<string[]>([])

  const { data: allergies } = useQuery({
    queryKey: ['allergies', patientId],
    queryFn: () => allergyService.listByPatient(patientId),
    enabled: !!patientId,
  })

  const { data: vaccineReference } = useQuery({
    queryKey: ['vaccine-reference', vaccineType],
    queryFn: () => vaccineReferenceService.getVaccineReference(vaccineType),
    enabled: !!vaccineType,
  })

  useEffect(() => {
    if (!allergies || allergies.length === 0 || !vaccineReference) {
      setWarnings([])
      onWarningsChange?.([])
      return
    }

    const activeAllergies = allergies.filter((a: any) => a.status === 'active')
    const newWarnings: string[] = []

    // Check each allergy against vaccine components
    for (const allergy of activeAllergies) {
      const allergyDisplay = (allergy.triggerDisplay || '').toLowerCase()
      
      for (const component of vaccineReference.components || []) {
        const componentName = component.name.toLowerCase()
        
        // Check for matches
        if (
          allergyDisplay.includes(componentName) ||
          componentName.includes(allergyDisplay) ||
          allergyDisplay === componentName
        ) {
          newWarnings.push(
            `Patient has allergy to ${allergy.triggerDisplay} which may be present in ${vaccineType} (${component.name})`
          )
        }
      }
    }

    setWarnings(newWarnings)
    onWarningsChange?.(newWarnings)
  }, [allergies, vaccineReference, vaccineType, onWarningsChange])

  if (!vaccineType) {
    return null
  }

  if (warnings.length === 0) {
    return (
      <div className="vaccine-allergy-checker">
        <div className="allergy-check-status success">
          ✓ No known allergies to vaccine components
        </div>
      </div>
    )
  }

  return (
    <div className="vaccine-allergy-checker">
      <div className="allergy-check-status warning">
        ⚠️ Allergy Warning
      </div>
      <ul className="allergy-warnings">
        {warnings.map((warning, index) => (
          <li key={index}>{warning}</li>
        ))}
      </ul>
      <div className="allergy-check-note">
        Please review patient allergies before administering this vaccine. Consider consulting with an allergist if there are concerns.
      </div>
    </div>
  )
}
